/*
 * Hero Video New — playback controller.
 *
 * Loaded once with `defer` and shared by every instance on the page, so the
 * behaviour below costs one cached request instead of a script block per
 * section.
 *
 * The time-critical part (choosing the right src and starting the fetch for an
 * above-the-fold hero) is NOT here — the section emits a ~6 line inline
 * bootstrap directly after the <video> tag so the request starts while the
 * HTML is still parsing. This file only picks up what can safely wait:
 *   - lazy-loading below-the-fold instances
 *   - swapping src when the viewport crosses the mobile/desktop breakpoint
 *   - pausing off-screen / background-tab video (saves CPU + bandwidth)
 *   - the mobile mute + fullscreen buttons
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 768px)';
  var REDUCED_MQ = '(prefers-reduced-motion: reduce)';

  function on(mq, handler) {
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
  }

  function HeroVideoNew(root) {
    this.root = root;
    this.video = root.querySelector('.heroVideoNew__video');
    if (!this.video) return;

    this.mq = window.matchMedia(MOBILE_MQ);
    this.reduced =
      root.getAttribute('data-respect-reduced-motion') === 'true' &&
      window.matchMedia(REDUCED_MQ).matches;
    this.eager = root.getAttribute('data-eager') === 'true';
    this.inView = true;

    this.bindMedia();
    this.bindControls();
  }

  HeroVideoNew.prototype.srcFor = function () {
    var d = this.video.getAttribute('data-src-desktop') || '';
    var m = this.video.getAttribute('data-src-mobile') || '';
    if (this.mq.matches && m) return m;
    return d || m;
  };

  HeroVideoNew.prototype.load = function () {
    var src = this.srcFor();
    if (!src || this.video.getAttribute('data-loaded') === src) return;
    this.video.setAttribute('data-loaded', src);
    this.video.src = src;
    this.video.load();
    if (!this.reduced) this.play();
  };

  HeroVideoNew.prototype.play = function () {
    if (this.reduced || !this.inView || document.hidden) return;
    var p = this.video.play();
    if (p && p.catch) p.catch(function () {});
  };

  HeroVideoNew.prototype.bindMedia = function () {
    var self = this;

    this.video.addEventListener('playing', function () {
      self.root.classList.add('is-playing');
    });

    // Reduced motion: never autoplay, leave the poster up and expose controls
    // so the visitor can start it themselves.
    if (this.reduced) {
      this.video.removeAttribute('autoplay');
      this.video.setAttribute('controls', '');
      this.video.setAttribute('preload', 'none');
      return;
    }

    if (this.eager || !('IntersectionObserver' in window)) {
      this.load();
    } else {
      // Below the fold: don't touch the network until it is nearly in view.
      var loader = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          loader.disconnect();
          self.load();
        },
        { rootMargin: '400px 0px' }
      );
      loader.observe(this.root);
    }

    // Swap source when crossing the breakpoint, but only if a distinct source
    // exists for the new one — otherwise we would needlessly restart playback.
    on(this.mq, function () {
      if (self.video.getAttribute('data-loaded')) self.load();
    });

    // Pause while off-screen; resume on the way back in.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          self.inView = entries[0].isIntersecting;
          if (self.inView) self.play();
          else self.video.pause();
        },
        { threshold: 0 }
      ).observe(this.root);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.video.pause();
      else self.play();
    });
  };

  HeroVideoNew.prototype.bindControls = function () {
    var self = this;
    var video = this.video;
    var muteBtn = this.root.querySelector('[data-hvn-mute]');
    var fsBtn = this.root.querySelector('[data-hvn-fullscreen]');

    // Native controls are a desktop-only affordance; on mobile the custom
    // round buttons take over so the control bar never covers the copy.
    var nativeDesktop = this.root.getAttribute('data-controls-desktop') === 'true';
    function applyNative() {
      if (self.reduced) return;
      if (!self.mq.matches && nativeDesktop) video.setAttribute('controls', '');
      else video.removeAttribute('controls');
    }
    applyNative();
    on(this.mq, applyNative);

    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        muteBtn.setAttribute('aria-pressed', String(!video.muted));
        muteBtn.setAttribute(
          'aria-label',
          video.muted ? 'Unmute video' : 'Mute video'
        );
        var off = muteBtn.querySelector('[data-hvn-icon="muted"]');
        var onI = muteBtn.querySelector('[data-hvn-icon="unmuted"]');
        if (off) off.style.display = video.muted ? 'block' : 'none';
        if (onI) onI.style.display = video.muted ? 'none' : 'block';
        if (!video.muted) self.play();
      });
    }

    if (fsBtn) {
      fsBtn.addEventListener('click', function () {
        if (video.requestFullscreen) video.requestFullscreen();
        else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      });
    }
  };

  function init(scope) {
    var nodes = (scope || document).querySelectorAll(
      '.heroVideoNew__container[data-hvn]'
    );
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].hvnBound) continue;
      nodes[i].hvnBound = true;
      new HeroVideoNew(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-bind when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
